import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bell,
  CalendarClock,
  Inbox,
  ListTodo,
  Sparkles,
  ArrowRight,
  Loader2,
} from "lucide-react";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";

const CARDS = [
  { key: "contacts", label: "Contact requests", icon: Inbox, to: "/admin/contact", accentKey: "contacts_unresolved", accentLabel: "unresolved" },
  { key: "signups", label: "Challenge signups", icon: Sparkles, to: "/admin/contact", accentKey: null },
  { key: "reminders", label: "Reminders", icon: ListTodo, to: "/admin/reminders", accentKey: "reminders_upcoming", accentLabel: "upcoming" },
  { key: "notifications", label: "Notifications", icon: Bell, to: "/admin/notifications" },
];

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await adminApi.summary();
        if (alive) setData(res);
      } catch (e) {
        toast.error("Couldn't load the dashboard.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-3" style={{ color: "#1B4332" }}>
        <Loader2 className="animate-spin" size={16} /> Loading&hellip;
      </div>
    );
  }

  const counts = data?.counts || {};

  return (
    <div data-testid="page-admin-dashboard" className="space-y-10">
      {/* Cards */}
      <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
        {CARDS.map((c) => (
          <StatCard
            key={c.key}
            to={c.to}
            icon={<c.icon size={18} />}
            label={c.label}
            value={counts[c.key] ?? 0}
            accent={c.accentKey ? counts[c.accentKey] : null}
            accentLabel={c.accentLabel}
          />
        ))}
      </div>

      {/* Split lists */}
      <div className="grid gap-6 md:grid-cols-2">
        <ListPanel
          title="Recent contact requests"
          to="/admin/contact"
          items={data?.recent_contacts || []}
          renderItem={(row) => (
            <div key={row.id} className="py-3 border-b last:border-none" style={{ borderColor: "rgba(27,67,50,0.08)" }}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium truncate" style={{ color: "#1B4332" }}>{row.name}</div>
                  <div className="text-xs truncate" style={{ color: "#6E6759" }}>{row.email} &middot; {row.topic}</div>
                  <div className="text-sm mt-1 line-clamp-2" style={{ color: "#2A2621" }}>{row.message}</div>
                </div>
                <span className="text-xs shrink-0" style={{ color: "#6E6759" }}>{formatDate(row.created_at)}</span>
              </div>
            </div>
          )}
          emptyText="No contact notes yet."
        />

        <ListPanel
          title="Upcoming reminders"
          to="/admin/reminders"
          items={data?.upcoming_reminders || []}
          renderItem={(row) => (
            <div key={row.id} className="py-3 border-b last:border-none" style={{ borderColor: "rgba(27,67,50,0.08)" }}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium truncate" style={{ color: "#1B4332" }}>{row.title}</div>
                  {row.notes && <div className="text-xs mt-1 line-clamp-2" style={{ color: "#6E6759" }}>{row.notes}</div>}
                </div>
                <span className="text-xs shrink-0" style={{ color: "#C9A84C" }}>{formatDate(row.due_at)}</span>
              </div>
            </div>
          )}
          emptyText="Nothing on the calendar."
        />

        <ListPanel
          title="Recent challenge signups"
          to="/admin/contact"
          items={data?.recent_signups || []}
          renderItem={(row) => (
            <div key={row.id} className="py-3 border-b last:border-none flex items-center justify-between gap-3" style={{ borderColor: "rgba(27,67,50,0.08)" }}>
              <div className="min-w-0">
                <div className="font-medium truncate" style={{ color: "#1B4332" }}>{row.name}</div>
                <div className="text-xs truncate" style={{ color: "#6E6759" }}>{row.email}</div>
              </div>
              <span className="text-xs shrink-0" style={{ color: "#6E6759" }}>{formatDate(row.created_at)}</span>
            </div>
          )}
          emptyText="No signups yet."
        />

        <ListPanel
          title="Recent notifications"
          to="/admin/notifications"
          items={data?.recent_notifications || []}
          renderItem={(row) => (
            <div key={row.id} className="py-3 border-b last:border-none" style={{ borderColor: "rgba(27,67,50,0.08)" }}>
              <div className="font-medium truncate" style={{ color: "#1B4332" }}>{row.title}</div>
              <div className="text-xs mt-1 line-clamp-2" style={{ color: "#6E6759" }}>{row.body}</div>
              <div className="text-[11px] mt-1" style={{ color: "#6E6759" }}>{formatDate(row.created_at)} &middot; {row.audience}</div>
            </div>
          )}
          emptyText="Nothing sent yet."
        />
      </div>

      {/* Appointments callout */}
      <div
        className="rounded-2xl p-8 flex flex-col md:flex-row items-start md:items-center gap-4 justify-between"
        style={{ background: "#1B4332", color: "#FAF6EC" }}
        data-testid="dashboard-appointments-callout"
      >
        <div className="flex items-start gap-4">
          <span className="inline-flex h-11 w-11 items-center justify-center rounded-full" style={{ background: "#C9A84C", color: "#1B4332" }}>
            <CalendarClock size={18} />
          </span>
          <div>
            <div className="font-serif text-2xl">Appointments</div>
            <div className="text-sm opacity-80">Book &amp; view discovery calls through your Google Appointment Schedule.</div>
          </div>
        </div>
        <Link to="/admin/appointments" className="btn-gold" data-testid="dashboard-open-appointments">
          Open appointments <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  );
}

function StatCard({ to, icon, label, value, accent, accentLabel }) {
  return (
    <Link
      to={to}
      className="rounded-2xl border p-5 flex flex-col gap-3 transition-all hover:-translate-y-0.5"
      style={{ background: "#fff", borderColor: "rgba(27,67,50,0.12)" }}
      data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-center gap-3">
        <span
          className="inline-flex h-9 w-9 items-center justify-center rounded-full"
          style={{ background: "#FAF6EC", color: "#1B4332", border: "1px solid rgba(201,168,76,0.5)" }}
        >
          {icon}
        </span>
        <span className="text-xs tracking-[0.2em] uppercase" style={{ color: "#6E6759" }}>{label}</span>
      </div>
      <div className="font-serif text-4xl" style={{ color: "#1B4332" }}>{value}</div>
      {accent != null && (
        <div className="text-xs" style={{ color: "#C9A84C" }}>
          {accent} {accentLabel}
        </div>
      )}
    </Link>
  );
}

function ListPanel({ title, to, items, renderItem, emptyText }) {
  return (
    <div
      className="rounded-2xl border p-6"
      style={{ background: "#fff", borderColor: "rgba(27,67,50,0.12)" }}
    >
      <div className="flex items-center justify-between">
        <h3 className="font-serif text-xl" style={{ color: "#1B4332" }}>{title}</h3>
        {to && (
          <Link to={to} className="text-xs" style={{ color: "#C9A84C" }}>
            View all
          </Link>
        )}
      </div>
      <div className="mt-3">
        {items.length === 0 ? (
          <div className="text-sm py-4" style={{ color: "#6E6759" }}>{emptyText}</div>
        ) : (
          items.map(renderItem)
        )}
      </div>
    </div>
  );
}
